import { forwardRef } from "react";
import {
  Box,
  Typography,
  makeStyles,
  Checkbox,
  FormGroup,
  FormControlLabel
} from "@material-ui/core";
import { Document, FileRef } from "@bonliva-crm/graphql";
import FolderIcon from "../../icons/components/folder-icon";
import { CheckedIcon, DraftIcon } from "../../icons";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { Color } from "../../theme";
import cns from "classnames";

type DocumentSlice = Pick<Document, "DocumentType" | "PreviewURL"> & {
  FileRef: Pick<FileRef, "NodeID" | "Name">;
};

type Props = {
  label?: string;
  files: DocumentSlice[];
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
};

export const DocumentList = forwardRef<HTMLDivElement, Props>(
  ({ label, files, value, onChange, error }, ref) => {
    const classes = useStyles();
    const documentsGroupedByType: Record<string, DocumentSlice[]> = {};

    files.forEach((document) => {
      const documentType = document.DocumentType ?? "Other";
      if (!documentsGroupedByType[documentType]) {
        documentsGroupedByType[documentType] = [document];
      } else {
        documentsGroupedByType[documentType].push(document);
      }
    });

    return (
      <div
        className={cns(classes.root, {
          [classes.error]: error
        })}
        ref={ref}
      >
        {label && <Typography variant="h6">{label}</Typography>}
        {Object.entries(documentsGroupedByType).map(([documentType, documents]) => {
          return (
            <FormGroup key={documentType}>
              <FormControlLabel
                control={
                  <Checkbox
                    name={documentType}
                    checked={documents.every((document) =>
                      value.includes(document.FileRef.NodeID)
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([
                          ...value,
                          ...documents.map((document) => document.FileRef.NodeID)
                        ]);
                      } else {
                        onChange(
                          value.filter(
                            (id) =>
                              !documents.some(
                                (document) => document.FileRef.NodeID === id
                              )
                          )
                        );
                      }
                    }}
                  />
                }
                label={
                  <Box className={classes.iconWithText}>
                    <FolderIcon />
                    <Typography variant="h6" style={{ paddingTop: "2px" }}>
                      {documentType}
                    </Typography>
                  </Box>
                }
              />
              <Box className={classes.documentRoot}>
                {documents.map((document) => {
                  return (
                    <Box className={classes.documentRow} key={document.FileRef.NodeID}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checkedIcon={<CheckedIcon htmlColor={Color.PrimaryLight} />}
                            name={document.FileRef.Name ?? undefined}
                            checked={value.includes(document.FileRef.NodeID)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onChange([...value, document.FileRef.NodeID]);
                              } else {
                                onChange(
                                  value.filter((id) => id !== document.FileRef.NodeID)
                                );
                              }
                            }}
                          />
                        }
                        label={
                          <Box className={classes.iconWithText}>
                            <DraftIcon htmlColor={Color.DetailsInput} />
                            <Typography variant="body1" style={{ paddingTop: "2px" }}>
                              {document.FileRef.Name}
                            </Typography>
                          </Box>
                        }
                      />
                      <OpenInNewIcon
                        className={classes.previewIcon}
                        fontSize={"small"}
                        htmlColor={Color.DetailsInput}
                        onClick={() => window.open(document.PreviewURL)}
                      />
                    </Box>
                  );
                })}
              </Box>
            </FormGroup>
          );
        })}
      </div>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    paddingLeft: "0.5rem"
  },
  documentRoot: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    paddingLeft: "1rem"
  },
  documentRow: {
    display: "flex",
    alignItems: "center"
  },
  iconWithText: {
    display: "flex",
    flexDirection: "row",
    gap: "0.25rem",
    alignItems: "center",
    paddingLeft: "0.25rem"
  },
  previewIcon: {
    cursor: "pointer"
  },

  error: {
    color: theme.palette.error.main
  }
}));

DocumentList.displayName = "DocumentList";

export default DocumentList;
